import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { WizardPhase } from "../state/types";

type PhaseStep = {
  phase: WizardPhase;
  label: string;
  index: number; // 1-based step index shown to the user
};

const PHASE_STEPS: PhaseStep[] = [
  { phase: "select", label: "Select", index: 1 },
  { phase: "review_suggested", label: "Review suggested", index: 2 },
  { phase: "manual_grouping", label: "Manual grouping", index: 3 },
  { phase: "final_review", label: "Final review", index: 4 },
  { phase: "uploading", label: "Upload", index: 5 },
];

function phaseOrderIndex(phase: WizardPhase): number {
  return PHASE_STEPS.findIndex((step: PhaseStep) => step.phase === phase);
}

type WizardShellProps = {
  phase: WizardPhase;
  title?: string;
  subtitle?: string;
  onDiscard?: () => void;
  /**
   * Whether to disable the discard button (e.g. while uploads are in
   * flight). The button is hidden if `onDiscard` is not provided.
   */
  discardDisabled?: boolean;
  children: ReactNode;
};

/**
 * Pure presentational shell for the customer-matching wizard.
 *
 * - Renders a sticky header with a title, optional subtitle, and an
 *   optional discard button.
 * - Renders a horizontal step indicator showing all five phases, with
 *   completed phases dimmed-but-present and the current phase
 *   highlighted.
 * - Wraps `children` in a scrollable, bounded container so the wizard
 *   can host long phase content without growing the page.
 *
 * No internal state, no reducer, no data loading.
 */
export function WizardShell({
  phase,
  title = "Create payment remittance batches",
  subtitle,
  onDiscard,
  discardDisabled,
  children,
}: WizardShellProps) {
  const currentOrder = phaseOrderIndex(phase);

  return (
    <div className="flex flex-col gap-4 p-4 h-[calc(100vh-6rem)] overflow-hidden">
      <div className="flex flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-extrabold tracking-tight lg:text-3xl">
            {title}
          </h1>
          {subtitle ? (
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        {onDiscard ? (
          <Button
            variant="outline"
            onClick={onDiscard}
            disabled={discardDisabled}
          >
            Discard wizard
          </Button>
        ) : null}
      </div>

      <ol
        className="flex flex-row flex-wrap gap-2 items-center"
        aria-label="Wizard phases"
      >
        {PHASE_STEPS.map((step: PhaseStep, idx: number) => {
          const isCurrent = step.phase === phase;
          const isComplete = idx < currentOrder;
          return (
            <li
              key={step.phase}
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "flex flex-row items-center gap-2 rounded-md border px-3 py-1.5 text-xs",
                isCurrent &&
                  "border-primary bg-primary/10 text-primary font-semibold",
                !isCurrent &&
                  isComplete &&
                  "border-muted bg-muted/30 text-muted-foreground",
                !isCurrent &&
                  !isComplete &&
                  "border-muted bg-card text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold",
                  isCurrent && "bg-primary text-primary-foreground",
                  !isCurrent && isComplete && "bg-muted-foreground/30",
                  !isCurrent && !isComplete && "bg-muted",
                )}
              >
                {step.index}
              </span>
              <span>{step.label}</span>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto rounded-md border bg-card p-4">
        {children}
      </div>
    </div>
  );
}
